import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import apiService from "../../ApiService";
import Select from "react-select";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MonthlyTransactionSummary = () => {
  // Remove month, use multi-year
  const [years, setYears] = useState([]);
  const [rawSummary, setRawSummary] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalTransactions, setModalTransactions] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // Set default years to current year
  useEffect(() => {
    const now = new Date();
    setYears([String(now.getFullYear())]);
  }, []);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line
  }, [years]);

  const fetchSummary = async () => {
    try {
      const data = await apiService.fetchTransactionMonthlySummary({
        years,
        country: "USA",
      });
      setRawSummary(Array.isArray(data) ? data : []);
    } catch (e) {
      setRawSummary([]);
    }
  };

  // Group and filter data for chart and totals
  const filtered = React.useMemo(() => {
    let arr = rawSummary;
    if (years && years.length > 0) {
      arr = arr.filter((t) =>
        years.includes(String((t.month || t.Month || "").split("-")[0]))
      );
    }
    const grouped = {};
    arr.forEach((item) => {
      const m = item.month || item.Month;
      if (!grouped[m]) grouped[m] = { credit: 0, debit: 0 };
      if ((item.transaction_type || item.TransactionType) === "credit") {
        grouped[m].credit += Math.abs(Number(item.total || item.Total || 0));
      } else if ((item.transaction_type || item.TransactionType) === "debit") {
        grouped[m].debit += Math.abs(Number(item.total || item.Total || 0));
      }
    });
    const months = Object.keys(grouped).sort();
    return months.map((m) => ({ month: m, ...grouped[m] }));
  }, [rawSummary, years]);

  const chartData = React.useMemo(() => {
    const months = filtered.map((item) => item.month);
    const credit = filtered.map((item) => item.credit);
    const debit = filtered.map((item) => item.debit);
    return {
      labels: months,
      datasets: [
        {
          label: "Credit",
          backgroundColor: "#22c55e",
          data: credit,
        },
        {
          label: "Debit",
          backgroundColor: "#ef4444",
          data: debit,
        },
      ],
    };
  }, [filtered]);

  // Calculate totals
  const totalCredit = filtered.reduce((sum, t) => sum + t.credit, 0);
  const totalDebit = filtered.reduce((sum, t) => sum + t.debit, 0);
  const overall = totalCredit - totalDebit;

  // Year options (multi-select)
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const y = new Date().getFullYear() - i;
    return { value: String(y), label: String(y) };
  });

  // Handle bar click/hover
  const handleBarClick = async (elements, event) => {
    if (!elements.length) return;
    const chart = elements[0]._chart || elements[0].element.$context.chart;
    const idx = elements[0]._index ?? elements[0].index;
    const datasetIdx = elements[0]._datasetIndex ?? elements[0].datasetIndex;
    const monthLabel = chart.data.labels[idx];
    const type = chart.data.datasets[datasetIdx].label.toLowerCase();
    setModalTitle(
      `${type.charAt(0).toUpperCase() + type.slice(1)} - ${monthLabel} (USA)`
    );
    setModalOpen(true);
    setModalLoading(true);
    // Calculate fromDate/toDate for the month
    const [y, m] = monthLabel.split("-");
    const fromDate = `${y}-${m}-01`;
    const toDate = new Date(Number(y), Number(m), 0);
    const toDateStr = `${y}-${m}-${String(toDate.getDate()).padStart(2, "0")}`;
    try {
      const txs = await apiService.fetchTransactions({
        fromDate,
        toDate: toDateStr,
        country: "USA",
        type,
      });
      setModalTransactions(
        Array.isArray(txs) ? txs : txs.data || txs.records || []
      );
    } catch (e) {
      setModalTransactions([]);
    }
    setModalLoading(false);
  };

  return (
    <div className="w-full flex flex-col bg-gray-50">
      <div className="sticky top-0 z-30 border-b px-0 py-0">
        <div className="flex flex-row items-center justify-between w-full px-6 py-3">
          <div className="flex flex-col flex-1 min-w-0">
            <h2 className="text-lg font-bold mb-1 text-left tracking-tight text-slate-700 drop-shadow-sm">
              Monthly Transaction Summary
            </h2>
            <div className="flex gap-4 text-[15px] font-medium mb-1">
              <div className="bg-green-50 text-green-700 rounded px-3 py-1 shadow-sm border border-green-200">
                Total Credit:{" "}
                <span className="font-bold">
                  {totalCredit.toLocaleString()}
                </span>
              </div>
              <div className="bg-red-50 text-red-700 rounded px-3 py-1 shadow-sm border border-red-200">
                Total Debit:{" "}
                <span className="font-bold">{totalDebit.toLocaleString()}</span>
              </div>
              <div
                className={
                  "rounded px-3 py-1 shadow-sm border " +
                  (overall >= 0
                    ? "bg-green-100 text-green-800 border-green-200"
                    : "bg-red-100 text-red-800 border-red-200")
                }
              >
                Overall:{" "}
                <span className="font-bold">{overall.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 items-end ml-4">
            <div className="min-w-[160px]">
              <label className="block mb-1 text-slate-700 font-medium">
                Year
              </label>
              <Select
                options={yearOptions}
                value={yearOptions.filter((y) => years.includes(y.value))}
                onChange={(opts) =>
                  setYears(opts ? opts.map((o) => o.value) : [])
                }
                isMulti
                isSearchable={false}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 28,
                    height: 32,
                    borderRadius: 8,
                    borderColor: "#cbd5e1",
                    boxShadow: "none",
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    minHeight: 28,
                    height: 28,
                    padding: "0 6px",
                  }),
                  input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  indicatorsContainer: (base) => ({ ...base, height: 28 }),
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center px-2 pt-2 pb-0 overflow-hidden">
        <div
          className="w-full max-w-3xl bg-white rounded shadow p-2"
          style={{ height: "calc(65vh)" }}
        >
          <div style={{ width: "100%", height: "100%" }}>
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: "top" },
                  title: { display: true, text: "Credit vs Debit (Monthly)" },
                },
                scales: {
                  y: { beginAtZero: true },
                },
                onClick: (event, elements) => handleBarClick(elements, event),
                onHover: (event, elements) => {
                  const target =
                    event &&
                    (event.native ? event.native.target : event.target);
                  if (target && target.style) {
                    if (elements && elements.length)
                      target.style.cursor = "pointer";
                    else target.style.cursor = "default";
                  }
                },
              }}
              height={320}
            />
          </div>
        </div>
      </div>

      {/* Modal for transaction details */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        contentLabel="Transaction Details"
        ariaHideApp={false}
        style={{
          overlay: { zIndex: 10000, background: "rgba(0,0,0,0.4)" },
          content: {
            maxWidth: 800,
            margin: "auto",
            borderRadius: 6,
            maxHeight: "70vh",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 2px 16px rgba(0,0,0,0.18)",
            background: "#f9f9f9",
          },
        }}
      >
        <div className="flex justify-between items-center px-5 pt-4 pb-2 border-b bg-white sticky top-0 z-20">
          <h3 className="text-lg font-bold">{modalTitle}</h3>
          <button
            className="text-2xl px-2 leading-none hover:text-red-600"
            onClick={() => setModalOpen(false)}
          >
            &times;
          </button>
        </div>
        <div className="flex-1 flex flex-col min-h-0">
          {modalLoading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : modalTransactions.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No transactions found.
            </div>
          ) : (
            <div
              className="flex-1 overflow-auto"
              style={{
                minHeight: 0,
                maxHeight: "60vh",
                background: "#fff",
              }}
            >
              <table className="w-full border border-gray-300 text-sm table-fixed">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="border-b px-3 py-2 text-left font-semibold whitespace-nowrap w-[110px]">
                      Date
                    </th>
                    <th className="border-b px-3 py-2 text-left font-semibold whitespace-nowrap w-[90px]">
                      Bank
                    </th>
                    <th className="border-b px-3 py-2 text-left font-semibold whitespace-nowrap w-[70px]">
                      Type
                    </th>
                    <th className="border-b px-3 py-2 text-right font-semibold whitespace-nowrap w-[90px]">
                      Amount
                    </th>
                    <th className="border-b px-3 py-2 text-left font-semibold whitespace-nowrap w-[120px]">
                      Category
                    </th>
                    <th className="border-b px-3 py-2 text-left font-semibold">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {modalTransactions.map((tx, idx) => {
                    let date = tx.transaction_date || tx.date || tx.Date || "";
                    // Format date as YYYY-MM-DD
                    if (date && date.length >= 10) date = date.slice(0, 10);
                    return (
                      <tr
                        key={tx.id || idx}
                        className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="border-b px-3 py-2 whitespace-nowrap font-mono text-xs">
                          {date}
                        </td>
                        <td className="border-b px-3 py-2 whitespace-nowrap">
                          {tx.bank_name || ""}
                        </td>
                        <td className="border-b px-3 py-2 capitalize whitespace-nowrap">
                          {tx.transaction_type || ""}
                        </td>
                        <td
                          className={`border-b px-3 py-2 text-right font-mono whitespace-nowrap ${
                            String(tx.amount || tx.Amount || "").startsWith("-")
                              ? "text-red-600"
                              : "text-green-700"
                          }`}
                        >
                          {tx.amount || tx.Amount || ""}
                        </td>
                        <td className="border-b px-3 py-2 whitespace-nowrap">
                          {tx.category || tx.Category || ""}
                        </td>
                        <td className="border-b px-3 py-2">
                          {tx.description || tx.Description || ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
      {/* ...totals moved to top... */}
    </div>
  );
};

export default MonthlyTransactionSummary;
